// Converts the local Item form state (`ItemFormState` in ItemDetail.tsx) into
// the wire-format `SaveItemInput`. Keeps the form free to use flat shapes
// (e.g. `firstExecutionKind` + `firstExecutionNDays`) while honoring the
// discriminated-union types the API expects.
//
// Pure — does not validate cross-field constraints. The mock/API layer owns
// the canonical validation; the form does Slice 13's pre-save checks.

import type {
  FirstExecution,
  GradeGuidelineMap,
  ProItemScope,
  ProMeasurementCycle,
  DayOfWeek,
  FirstExecutionKind,
  SaveItemInput,
} from '../types/pro-item-design.types';

export interface FormToPayloadInput {
  name: string;
  description: string;
  scope: ProItemScope;
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
  cycle: ProMeasurementCycle;
  daysOfWeek: DayOfWeek[];
  firstExecutionKind: FirstExecutionKind;
  firstExecutionNDays: number;
  // COMPLEX mode was removed per ADM.036 (SHCT-391) — SUM is the only method.
  gradeSumG1Max: number;
  gradeSumG2Max: number;
  gradeGuidelines: GradeGuidelineMap;
}

function buildFirstExecution(
  kind: FirstExecutionKind,
  nDays: number,
): FirstExecution {
  if (kind === 'N_DAYS_AFTER') {
    return { kind, nDays };
  }
  return { kind };
}

export function formToSavePayload(form: FormToPayloadInput): SaveItemInput {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    scope: form.scope,
    diseaseGroupId:
      form.scope === 'DISEASE_GROUP' ? form.diseaseGroupId : null,
    diseaseCodeId:
      form.scope === 'DISEASE_SPECIFIC' ? form.diseaseCodeId : null,
    schedule: {
      cycle: form.cycle,
      daysOfWeek: [...form.daysOfWeek],
      firstExecution: buildFirstExecution(
        form.firstExecutionKind,
        form.firstExecutionNDays,
      ),
    },
    // SUM is the only grade-calculation method (ADM.036 / SHCT-391). The wire
    // fields are kept for BE-contract compatibility: always SUM, complex null.
    gradeMethod: 'SUM',
    gradeSum: { g1Max: form.gradeSumG1Max, g2Max: form.gradeSumG2Max },
    gradeComplex: null,
    gradeGuidelines: { ...form.gradeGuidelines },
  };
}
